import openai from '../../../lib/openaiClient';

/**
 * Generates AI-powered buying guide content with advanced parameters
 * @param {Object} params - Guide parameters
 * @returns {Promise<Object>} Generated buying guide content
 */
export async function generateBuyingGuide(params) {
  try {
    const { 
      title, 
      category, 
      targetAudience, 
      focusPoints, 
      priceRange, 
      seasonalRelevance,
      guideLength = 'comprehensive',
      complexity = 'intermediate',
      tone = 'professional-friendly'
    } = params;

    // Word count mapping
    const wordCountMap = {
      'brief': '800-1200 words',
      'comprehensive': '1500-2000 words',
      'detailed': '2500-3000 words'
    };

    // Complexity instructions
    const complexityMap = {
      'beginner': 'Use simple language, explain technical terms, avoid jargon. Focus on practical basics.',
      'intermediate': 'Balance technical details with accessibility. Assume basic product knowledge.',
      'advanced': 'Include technical specifications, advanced features, and expert-level comparisons.'
    };

    // Tone instructions
    const toneMap = {
      'professional-friendly': 'Professional yet approachable. Authoritative but warm and helpful.',
      'casual': 'Conversational and relatable. Use everyday language and personal anecdotes.',
      'authoritative': 'Expert and formal. Data-driven with industry terminology and technical precision.'
    };

    const prompt = `Create a comprehensive buying guide for vacuum cleaners with the following specifications:

Title: ${title}
Category: ${category}
Target Audience: ${targetAudience}
${focusPoints ? `Focus Points: ${focusPoints}` : ''}
${priceRange ? `Price Range: ${priceRange}` : ''}
${seasonalRelevance ? `Seasonal Relevance: ${seasonalRelevance}` : ''}

GUIDE PARAMETERS:
- Length: ${wordCountMap?.[guideLength]}
- Complexity: ${complexityMap?.[complexity]}
- Tone: ${toneMap?.[tone]}

Generate a complete buying guide with:

1. ENGAGING INTRODUCTION (150-200 words)
   - Hook the reader with a relatable scenario or compelling statistic
   - Explain why this guide is valuable for the target audience
   - Preview what they'll learn and how it will help them
   - Set expectations for the guide's depth and coverage

2. MAIN CONTENT (${wordCountMap?.[guideLength]})
   - Key factors to consider when buying (prioritize based on focus points)
   - Detailed explanations of important features and technologies
   - Common mistakes to avoid (specific to target audience)
   - Expert recommendations and industry insights
   - Comparison of different options and approaches
   - Real-world use cases and practical scenarios
   - Budget considerations and value optimization
   ${priceRange ? `- Specific recommendations within ${priceRange} price range` : ''}
   ${seasonalRelevance ? `- Seasonal considerations for ${seasonalRelevance}` : ''}

3. PRODUCT RECOMMENDATIONS (3-5 products)
   - Top product suggestions with detailed explanations
   - Why each product is recommended for this audience
   - Best use cases and ideal scenarios for each
   - Price-to-value analysis
   - Pros and cons for each recommendation

4. BUYING TIPS & STRATEGIES
   - When to buy (seasonal considerations, sales events, best timing)
   - Where to buy (online vs retail, trusted retailers)
   - Warranty and return policy considerations
   - Budget optimization strategies
   - Negotiation tips and discount opportunities

5. FREQUENTLY ASKED QUESTIONS (5-7 questions)
   - Address common concerns specific to target audience
   - Provide detailed, actionable answers
   - Include troubleshooting and decision-making guidance

6. CONCLUSION (100-150 words)
   - Summarize key takeaways and main recommendations
   - Reinforce the most important decision factors
   - Provide final guidance for making the purchase
   - Include a clear call to action

IMPORTANT GUIDELINES:
- Write in ${toneMap?.[tone]} style
- ${complexityMap?.[complexity]}
- Use clear headings, subheadings, and bullet points for readability
- Focus on practical, actionable advice that helps readers make informed decisions
- Include specific examples and real-world scenarios
- Maintain consistency with the target audience's needs and knowledge level
- Ensure content is SEO-optimized with natural keyword integration
- Provide data-driven insights where applicable`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [
        { 
          role: 'system', 
          content: `You are an expert consumer product advisor and content writer specializing in home appliances and vacuum cleaners. You write comprehensive, SEO-optimized buying guides that combine technical knowledge with practical consumer insights. Your guides are trusted by thousands of readers making purchasing decisions. You adapt your writing style to match the specified tone and complexity level while maintaining accuracy and helpfulness.` 
        },
        { role: 'user', content: prompt },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'buying_guide',
          schema: {
            type: 'object',
            properties: {
              title: { type: 'string' },
              summary: { type: 'string' },
              content: { type: 'string' },
              keyPoints: {
                type: 'array',
                items: { type: 'string' },
                minItems: 4,
                maxItems: 8
              },
              recommendedProducts: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    name: { type: 'string' },
                    reason: { type: 'string' },
                    bestFor: { type: 'string' }
                  },
                  required: ['name', 'reason', 'bestFor']
                }
              },
              seoTitle: { type: 'string' },
              seoDescription: { type: 'string' },
              seoKeywords: {
                type: 'array',
                items: { type: 'string' }
              },
              readTimeMinutes: { type: 'number' }
            },
            required: ['title', 'summary', 'content', 'keyPoints', 'recommendedProducts', 'seoTitle', 'seoDescription', 'seoKeywords', 'readTimeMinutes'],
            additionalProperties: false
          }
        }
      },
    });

    const guideContent = JSON.parse(response?.choices?.[0]?.message?.content);
    return guideContent;
  } catch (error) {
    console.error('Error generating buying guide:', error);
    throw error;
  }
}
